import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import {
  COMMAND,
  GAUGE,
  GAUGE_RANGE,
  IMAGE,
  LOCATION,
  MAINTENANCE,
  MAP,
  TEXT_ENGINE,
  TEXT_FUEL,
  TEXT_STATIC,
  TEXT_TAGS,
} from '../../Constants/WidgetTypes';
import ModalLayout from '../ModalLayout';
import {
  AddDataRow,
  BarUtilizationTimeAggregation,
  CommandsCard,
  ConfigureAxisMaxMin,
  DisplayTextAsGridToggle,
  SignalsCard,
  TagsCard,
  TextStaticConfig,
  TimeSinceLastUpdatedToggle,
} from './WidgetModalDisplayComponents';
import ConfigureGaugeWidget from './ConfigureGaugeWidget';
import ConfigureImageWidget from './ConfigureImageWidget';
import { BASIC_SELECT_STYLES } from '../../Styles/SelectStyles';

export default memo(EditWidgetModalDisplay);

const animatedComponents = makeAnimated();
function EditWidgetModalDisplay(props) {
  const {
    asset,
    activeImageCoordinate,
    datapointsMap,
    displayTextAsGrid,
    editRequestPending,
    editWidget,
    error,
    handleAddImageOverlaySignal,
    handleAddRange,
    handleAddStaticTextLink,
    handleAddWidgetCommand,
    handleAddWidgetSignal,
    handleAggregationSelect,
    handleColorInput,
    handleColorSelected,
    handleCoordinateClick,
    handleDatapointSelect,
    handleDisplayTextAsGridClick,
    handleImageClick,
    handleInstanceSelect,
    handleMinMaxValueUpdate,
    handleNameAndYScaleUpdate,
    handleOptionDisabled,
    handleRangeValueUpdate,
    handleRemoveImageOverlaySignal,
    handleRemoveRange,
    handleRemoveStaticTextLink,
    handleRemoveWidgetSignal,
    handleSignalSelect,
    handleStaticTextInput,
    handleStaticTextLinkInput,
    handleTagSelect,
    handleTimeSinceLastUpdatedClick,
    image,
    imageCoordinatesMap,
    imageOverlayRef,
    isOpen,
    maintenanceLoading,
    reminderInstanceOptions,
    selectedAggregation,
    selectedInstances,
    showTimeSinceLastUpdated,
    tagsLoaded,
    tagsLoading,
    tagOptions,
    timeAggregationOptions,
    toggleImageOverlayModal,
    toggleModal,
    updateState,
    widgetName,
    widgetSignals,
    widgetType,
    yMax,
    yMin,
  } = props;

  const hideSignals = useMemo(
    () =>
      new Set([IMAGE, LOCATION, MAP, TEXT_ENGINE, TEXT_FUEL]).has(
        widgetType?.value
      ),
    [widgetType?.value]
  );

  return (
    <ModalLayout isOpen={isOpen} toggle={toggleModal} scrollable>
      <ModalHeader toggle={toggleModal}>Edit Smart Tile</ModalHeader>
      <ModalBody className="p-3 py-md-2 px-md-4 nine-tenth-text container-fluid d-flex flex-column">
        <div className="row">
          <div className="form-group col-12 mt-4">
            <label htmlFor="widgetName">Smart Tile Name</label>
            <input
              name="widgetName"
              type="text"
              className="form-control form-control-sm"
              value={widgetName}
              onChange={handleNameAndYScaleUpdate}
            />
          </div>
        </div>
        <div className="row">
          <div className="form-group col-12 mt-4">
            <div className="mb-2">Smart Tile Type</div>
            <div>
              <span className="text-brand-primary">{widgetType?.label}</span>
              <span className="text-muted pl-2">{widgetType?.helpText}</span>
            </div>
          </div>
        </div>
        <ConfigureAxisMaxMin
          handleNameAndYScaleUpdate={handleNameAndYScaleUpdate}
          widgetType={widgetType?.value}
          yMax={yMax}
          yMin={yMin}
        />
        <AddDataRow
          handleAddWidgetSignal={handleAddWidgetSignal}
          selectedType={widgetType}
          widgetSignals={widgetSignals}
          widgetType={widgetType?.value}
        />
        <BarUtilizationTimeAggregation
          handleAggregationSelect={handleAggregationSelect}
          isEdit
          selectedAggregation={selectedAggregation}
          timeAggregationOptions={timeAggregationOptions}
          widgetType={widgetType?.value}
        />
        <TimeSinceLastUpdatedToggle
          handleTimeSinceLastUpdatedClick={handleTimeSinceLastUpdatedClick}
          showTimeSinceLastUpdated={showTimeSinceLastUpdated}
          widgetType={widgetType?.value}
        />
        <DisplayTextAsGridToggle
          displayTextAsGrid={displayTextAsGrid}
          handleDisplayTextAsGridClick={handleDisplayTextAsGridClick}
          widgetType={widgetType?.value}
        />
        {widgetType?.value === IMAGE ? (
          <ConfigureImageWidget
            activeImageCoordinate={activeImageCoordinate}
            datapointsMap={datapointsMap}
            gtemplateID={asset?.gtemplateID}
            handleAddImageOverlaySignal={handleAddImageOverlaySignal}
            handleCoordinateClick={handleCoordinateClick}
            handleImageClick={handleImageClick}
            handleRemoveImageOverlaySignal={handleRemoveImageOverlaySignal}
            image={image}
            imageCoordinatesMap={imageCoordinatesMap}
            imageOverlayRef={imageOverlayRef}
            isEdit
            orgID={asset?.orgID}
            toggleImageOverlayModal={toggleImageOverlayModal}
            widgetType={widgetType?.value}
          />
        ) : null}
        <div
          className={`row position-relative ${
            widgetType?.value === IMAGE ? '' : 'flex-fill'
          }`}
        >
          <div
            className={
              hideSignals
                ? 'd-none'
                : 'overflow-auto col-12 h-100 signals-inner-container'
            }
          >
            {widgetType?.value === MAINTENANCE ? (
              <Select
                components={animatedComponents}
                isDisabled={maintenanceLoading}
                isMulti
                isSearchable
                onChange={handleInstanceSelect}
                options={reminderInstanceOptions}
                placeholder="Search by Reminder Name"
                styles={BASIC_SELECT_STYLES}
                value={selectedInstances}
              />
            ) : (
              widgetSignals.map((s, i) => {
                switch (widgetType?.value) {
                  case COMMAND:
                    return (
                      <CommandsCard
                        asset={asset}
                        command={s}
                        handleAddCommand={handleAddWidgetCommand}
                        handleRemoveCommand={handleRemoveWidgetSignal}
                        index={i}
                        key={s?.command?.value ?? i}
                        selectedCommands={widgetSignals}
                      />
                    );
                  case GAUGE:
                  case GAUGE_RANGE:
                    return (
                      <ConfigureGaugeWidget
                        datapointsMap={datapointsMap}
                        gtemplateID={asset?.gtemplateID}
                        handleAddRange={handleAddRange}
                        handleColorInput={handleColorInput}
                        handleColorSelected={handleColorSelected}
                        handleDatapointSelect={handleDatapointSelect}
                        handleMinMaxValueUpdate={handleMinMaxValueUpdate}
                        handleOptionDisabled={handleOptionDisabled}
                        handleRangeValueUpdate={handleRangeValueUpdate}
                        handleRemoveRange={handleRemoveRange}
                        handleRemoveWidgetSignal={handleRemoveWidgetSignal}
                        handleSignalSelect={handleSignalSelect}
                        index={i}
                        orgID={asset?.orgID}
                        key={i}
                        row={s}
                        updateState={updateState}
                        widgetType={widgetType?.value}
                      />
                    );
                  case TEXT_STATIC:
                    return (
                      <TextStaticConfig
                        handleAddStaticTextLink={handleAddStaticTextLink}
                        handleRemoveStaticTextLink={handleRemoveStaticTextLink}
                        handleStaticTextInput={handleStaticTextInput}
                        handleStaticTextLinkInput={handleStaticTextLinkInput}
                        key={i}
                        widgetSignals={widgetSignals}
                      />
                    );
                  case TEXT_TAGS:
                    return (
                      <TagsCard
                        handleRemoveTag={handleRemoveWidgetSignal}
                        handleTagSelect={handleTagSelect}
                        index={i}
                        key={i}
                        row={s}
                        selectedTags={widgetSignals}
                        tagOptions={tagOptions}
                        tagsLoaded={tagsLoaded}
                        tagsLoading={tagsLoading}
                      />
                    );
                  default:
                    return (
                      <SignalsCard
                        datapointsMap={datapointsMap}
                        gtemplateID={asset?.gtemplateID}
                        handleDatapointSelect={handleDatapointSelect}
                        handleOptionDisabled={handleOptionDisabled}
                        handleRemoveWidgetSignal={handleRemoveWidgetSignal}
                        handleSignalSelect={handleSignalSelect}
                        index={i}
                        key={i}
                        orgID={asset?.orgID}
                        row={s}
                        updateState={updateState}
                      />
                    );
                }
              })
            )}
          </div>
        </div>
        <div className={error ? 'row justify-content-end' : 'd-none'}>
          <p className="col-auto text-danger mb-0">{error}</p>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-outline-secondary py-0 px-3 mr-3"
          onClick={toggleModal}
        >
          Cancel
        </button>
        <button
          className="btn modal-button-submit py-0 px-4"
          onClick={editWidget}
        >
          {editRequestPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            'Save'
          )}
        </button>
      </ModalFooter>
      <style jsx>{`
        .btn {
          font-size: 0.75rem;
        }
        .remove-condition {
          color: var(--icon-link-gray) !important;
        }

        .remove-condition:hover {
          color: var(--text-light-gray) !important;
        }
        @media (min-width: 768px) {
          .signals-inner-container {
            position: absolute;
          }
        }
      `}</style>
    </ModalLayout>
  );
}

EditWidgetModalDisplay.propTypes = {
  asset: PropTypes.object,
  activeImageCoordinate: PropTypes.object,
  datapointsMap: PropTypes.arrayOf(PropTypes.object),
  displayTextAsGrid: PropTypes.bool,
  editRequestPending: PropTypes.bool,
  editWidget: PropTypes.func,
  error: PropTypes.string,
  handleAddImageOverlaySignal: PropTypes.func,
  handleAddRange: PropTypes.func,
  handleAddStaticTextLink: PropTypes.func,
  handleAddWidgetCommand: PropTypes.func,
  handleAddWidgetSignal: PropTypes.func,
  handleAggregationSelect: PropTypes.func,
  handleColorInput: PropTypes.func,
  handleColorSelected: PropTypes.func,
  handleCoordinateClick: PropTypes.func,
  handleDatapointSelect: PropTypes.func,
  handleDisplayTextAsGridClick: PropTypes.func,
  handleImageClick: PropTypes.func,
  handleInstanceSelect: PropTypes.func,
  handleMinMaxValueUpdate: PropTypes.func,
  handleNameAndYScaleUpdate: PropTypes.func,
  handleOptionDisabled: PropTypes.func,
  handleRangeValueUpdate: PropTypes.func,
  handleRemoveImageOverlaySignal: PropTypes.func,
  handleRemoveRange: PropTypes.func,
  handleRemoveStaticTextLink: PropTypes.func,
  handleRemoveWidgetSignal: PropTypes.func,
  handleSignalSelect: PropTypes.func,
  handleStaticTextInput: PropTypes.func,
  handleStaticTextLinkInput: PropTypes.func,
  handleTagSelect: PropTypes.func,
  handleTimeSinceLastUpdatedClick: PropTypes.func,
  image: PropTypes.string,
  imageCoordinatesMap: PropTypes.object,
  imageOverlayRef: PropTypes.object,
  isOpen: PropTypes.bool,
  maintenanceLoading: PropTypes.bool,
  maxValue: PropTypes.number,
  reminderInstanceOptions: PropTypes.arrayOf(PropTypes.object),
  selectedAggregation: PropTypes.string,
  selectedInstances: PropTypes.arrayOf(PropTypes.object),
  showTimeSinceLastUpdated: PropTypes.bool,
  tagOptions: PropTypes.arrayOf(PropTypes.object),
  tagsLoaded: PropTypes.bool,
  tagsLoading: PropTypes.bool,
  timeAggregationOptions: PropTypes.arrayOf(PropTypes.object),
  toggleImageOverlayModal: PropTypes.func,
  toggleModal: PropTypes.func,
  updateState: PropTypes.func,
  widgetName: PropTypes.string,
  widgetSignals: PropTypes.arrayOf(PropTypes.object),
  widgetType: PropTypes.object,
  yMax: PropTypes.number,
  yMin: PropTypes.number,
};

EditWidgetModalDisplay.defaultProps = { widgetSignals: [] };
